import {
  Button,
  Modal,
  Select as FBSelect,
  Textarea,
  TextInput,
  Radio,
} from "flowbite-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../Reducers/store";
import { formatDateInputs, formatNumbers, onlyNumberInput } from "../../Utils";
import { fetchFrequencies, fetchUsers } from "../../Actions/CommonFields";
import { Job } from "../../Models/Job";
import { ClientLocation } from "../../Models/ClientLocation";
import JobApi from "../../Api/JobApi";
import Loader from "../Common/Loader";
import JobItemListTable from "./JobItemListTable";
import { JobItem } from "../../Models/JobItem";
import { HiPlus } from "react-icons/hi";
import JobItemModal from "./JobItemModal";
import ClientLocationModal from "../Client/ClientLocationModal";
import { Client } from "../../Models/Client";
import ClientApi from "../../Api/ClientApi";
import DeliveryApi from "../../Api/DeliveryApi";
import { UserLevelNames } from "../../Models/UserLevel";
import UserApi from "../../Api/UserApi";
import { UserClockIn } from "../../Models/UserClockIn";
import InspectionApi from "../../Api/InspectionApi";
import { AssetTypeEnum } from "../../Models/AssetType";
import { Inspection, InspectionTypeEnum } from "../../Models/Inspection";
import DriverClockInOut from "../User/Driver/DriverClockInOut";
import { InspectionAsset } from "../../Models/InspectionAsset";
import SendNotificationModal from "./SendNotificationModal";

export default function JobModal({
  show,
  setShow,
  getJobs = undefined,
  getSchedulerData = undefined,
  clickedDate = undefined,
  selectedJob,
  setSelectedJob,
  onDeliveryStarted,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  getJobs?: () => Promise<void> | undefined;
  getSchedulerData?: () => void;
  clickedDate?: Date | null;
  selectedJob: Job | null;
  setSelectedJob: React.Dispatch<React.SetStateAction<Job | null>>;
  onDeliveryStarted?: (deliveryId: number, jobId: number) => void;
}) {
  const [showClockInOut, setshowClockInOut] = useState<boolean>(false);
  const { userModel } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { users, frequencies } = useAppSelector((state) => state.commonFields);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedClientLocation, setselectedClientLocation] =
    useState<ClientLocation | null>(null);
  const [showAddNewLocationModal, setshowAddNewLocationModal] =
    useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [clientList, setclientList] = useState<Client[]>([]);
  const [clientLocationList, setclientLocationList] = useState<
    ClientLocation[]
  >([]);

  const [formData, setFormData] = useState<Job>({
    id: 0,
    clientLocationId: null,
    dateCreated: null,
    jobNumber: null,
    startDate: "",
    endDate: "",
    maxAmount: null,
    terms: null,
    notes: null,
    clientId: null,
    userId: null,
    frequencyId: null,
    day: null,
    isRecurring: false,
    deleted: false,
    client: null,
    jobItems: [],
    clientLocation: null,
    frequency: null,
    user: null,
  });

  // Separate state for date and time
  const [startDateOnly, setStartDateOnly] = useState<string>("");
  const [dropTime, setDropTime] = useState<string>("");

  // Utility functions for date/time handling
  const splitDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: "", time: "" };

    // If it's already in YYYY-MM-DDTHH:mm format, split directly
    if (dateTimeString.includes("T") && dateTimeString.length >= 16) {
      const [datePart, timePart] = dateTimeString.split("T");
      const time = timePart.slice(0, 5); // Take only HH:mm part
      return { date: datePart, time: time };
    }

    // Otherwise, try to parse as Date and extract components
    const dt = new Date(dateTimeString);
    if (isNaN(dt.getTime())) return { date: "", time: "" };

    const pad = (n: number) => String(n).padStart(2, "0");
    const date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
      dt.getDate()
    )}`;
    const time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;

    return { date, time };
  };

  const combineDateTime = (date: string, time: string) => {
    if (!date) return "";
    if (!time) time = "00:00";

    // Create the datetime string in local format (YYYY-MM-DDTHH:mm)
    // This prevents timezone conversion issues
    const combinedDateTime = `${date}T${time}`;

    // Validate the format
    const testDate = new Date(combinedDateTime);
    if (isNaN(testDate.getTime())) return "";

    // Return the local datetime string without timezone conversion
    return combinedDateTime;
  };

  const getClients = () => {
    try {
      return ClientApi.getClientsList()
        .then((res) => {
          setclientList(res.data);
        })
        .catch((err) => {
          toast.error("Error");
        });
    } catch (error) {
      console.error(error);
      toast.error("Error");
    }
  };
  const getClientLocations = (clientId: number) => {
    try {
      return ClientApi.getClientLocationsForClient(clientId)
        .then((res) => {
          setclientLocationList(res.data);
        })
        .catch((err) => {
          toast.error("Error");
        });
    } catch (error) {
      console.error(error);
      toast.error("Error");
    }
  };

  const deleteJob = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteJob = () => {
    try {
      if (selectedJob) {
        return JobApi.softDeleteJob(selectedJob.id)
          .then((res) => {
            if (res.data.success) {
              toast.success("Job deleted successfully");
              if (getJobs) {
                getJobs();
              }
              if (getSchedulerData) {
                getSchedulerData();
              }
              setShowDeleteConfirm(false);
              onCloseModal();
            } else {
              toast.error(res.data.message || "Error deleting job");
            }
          })
          .catch((err) => {
            toast.error("Error");
          });
      } else {
        toast.error("No job selected for deletion");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error");
    }
  };
  const getReferences = async () => {
    setIsLoading(true);
    if (!users.length || !frequencies.length) {
      await Promise.all([dispatch(fetchUsers()), dispatch(fetchFrequencies())]);
    }
    getClients();
    setIsLoading(false);
  };

  const [userClockIn, setuserClockIn] = useState<UserClockIn | null>(null);

  const getLatestUserClockIn = async () => {
    if (userModel?.id) {
      return UserApi.getLatestUserClockIn(userModel?.id)
        .then((res) => {
          setuserClockIn(res.data);
        })
        .catch((err) => {
          toast.error("Error: " + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    }
  };

  const [latestInspection, setlatestInspection] = useState<Inspection | null>(
    null
  );
  const [latestInspectionTruck, setlatestInspectionTruck] =
    useState<InspectionAsset | null>(null);
  const [latestInspectionTrailer, setlatestInspectionTrailer] =
    useState<InspectionAsset | null>(null);
  const getUserLatestInspection = async () => {
    if (userModel?.id) {
      return InspectionApi.getUserLatestInspection(userModel?.id)
        .then((res) => {
          if (res.data) {
            //console.log(res.data);
            const latestTruck =
              res.data.inspectionAssets.find(
                (x) => x.asset?.typeId == AssetTypeEnum.Truck
              ) ?? null;
            const latestTrailer =
              res.data.inspectionAssets.find(
                (x) => x.asset?.typeId == AssetTypeEnum.Trailer
              ) ?? null;

            if (
              res.data.typeId === InspectionTypeEnum.Post &&
              res.data.mileage &&
              res.data.mileage > 0 &&
              latestTruck?.sopformId &&
              latestTruck?.sopformId > 0 &&
              latestTrailer?.sopformId &&
              latestTrailer?.sopformId > 0
            ) {
              if (checkIsDriverAccess()) {
                //console.log("reset");
                toast.error("Please complete your Pre-Trip Inspection...");
              }
              //alert();
              //handleResetFormValues();
            } else {
              //console.log("set data");
              // setFormData(res.data);
              setlatestInspection(res.data);
              setlatestInspectionTruck(latestTruck);
              setlatestInspectionTrailer(latestTrailer);
            }
          }
        })
        .catch((err) => {
          toast.error("Error: " + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    }
  };

  useEffect(() => {
    if (show) {
      getReferences();
      getLatestUserClockIn();
      getUserLatestInspection();
    }
  }, [show]);

  useEffect(() => {
    getUserLatestInspection();
  }, [showClockInOut]);

  const handleSetInitialValues = () => {
    const values = selectedJob
      ? {
          ...selectedJob,
        }
      : {
          id: 0,
          clientLocationId: null,
          dateCreated: null,
          jobNumber: null,
          startDate: "",
          endDate: "",
          maxAmount: null,
          terms: null,
          notes: null,
          clientId: null,
          userId: null,
          frequencyId: null,
          day: null,
          isRecurring: false,

          client: null,
          jobItems: [],
          clientLocation: null,
          frequency: null,
          user: null,
        };
    if (values.clientId) {
      getClientLocations(values.clientId);
    }

    // Handle clicked date
    if (clickedDate && !selectedJob) {
      values.startDate = formatDateInputs(clickedDate.toISOString());
    }

    setFormData(values as Job);

    // Split the startDate into separate date and time fields
    const startDateValue = values.startDate?.toString() || "";
    const { date, time } = splitDateTime(startDateValue);
    setStartDateOnly(date);
    setDropTime(time);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [selectedJob]);

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      clientLocationId: null,
      dateCreated: null,
      jobNumber: null,
      startDate: "",
      endDate: "",
      maxAmount: null,
      terms: null,
      notes: null,
      clientId: null,
      userId: null,
      frequencyId: null,
      day: null,
      isRecurring: false,
      deleted: false,

      client: null,
      jobItems: [],
      clientLocation: null,
      frequency: null,
      user: null,
    };
    setFormData(values as Job);

    // Reset separate date and time fields
    setStartDateOnly("");
    setDropTime("");

    setButtonDisabled(false);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "isRecurring") {
      setFormData((prevData) => ({ ...prevData, [name]: value === "true" }));
      setFormData((prevData) => ({ ...prevData, endDate: "" }));
      setFormData((prevData) => ({ ...prevData, frequencyId: null }));
      setFormData((prevData) => ({ ...prevData, day: null }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handlers for separate date and time fields
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setStartDateOnly(newDate);

    // Update the combined startDate in formData
    const combinedDateTime = combineDateTime(newDate, dropTime);
    setFormData((prevData) => ({ ...prevData, startDate: combinedDateTime }));
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setDropTime(newTime);

    // Update the combined startDate in formData
    const combinedDateTime = combineDateTime(startDateOnly, newTime);
    setFormData((prevData) => ({ ...prevData, startDate: combinedDateTime }));
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === "clientId") {
      setFormData((prevData) => ({ ...prevData, clientLocationId: null }));
      if (value) {
        getClientLocations(Number(value));
      } else {
        setclientLocationList([]);
      }
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeCurrencyInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const formattedValue = formatNumbers(value);
    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  function cleanMoneyValues(formData: Job) {
    formData.maxAmount = Number(
      (formData.maxAmount ?? 0).toString().replace(",", "")
    );
  }
  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    setIsLoading(true);
    cleanMoneyValues(formData);

    // Ensure startDate is sent with timezone offset to preserve local time
    if (formData.startDate) {
      // Convert to local datetime with timezone offset
      const date = new Date(formData.startDate);
      const pad = (n: number) => String(n).padStart(2, "0");

      // Get timezone offset in minutes and convert to HH:MM format
      const offsetMinutes = date.getTimezoneOffset();
      const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
      const offsetMins = Math.abs(offsetMinutes) % 60;
      const offsetSign = offsetMinutes <= 0 ? "+" : "-";
      const timezoneOffset = `${offsetSign}${pad(offsetHours)}:${pad(
        offsetMins
      )}`;

      // Create ISO string with timezone offset
      formData.startDate = `${date.getFullYear()}-${pad(
        date.getMonth() + 1
      )}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
        date.getMinutes()
      )}:00${timezoneOffset}`;
      console.log("Sending startDate with timezone:", formData.startDate);
    } else {
      formData.startDate = undefined;
    }

    if (!formData.endDate) {
      formData.endDate = undefined;
    }
    if (!formData.userId) {
      formData.userId = null;
    }
    if (!formData.day) {
      formData.day = null;
    }
    return await JobApi.addUpdateJob(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success("Save Success");
          setIsLoading(false);
          setButtonDisabled(false);
          handleResetFormValues();
          if (getJobs) {
            getJobs();
          }
          if (getSchedulerData) {
            getSchedulerData();
          }
          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error: " + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    handleResetFormValues();
    setclientLocationList([]);
    setSelectedJob(null);
    setShow(false);
  }

  const createDelivery = async () => {
    if (!checkDriverReady()) {
      setshowClockInOut(true);
      toast.error("Please complete your Pre-Trip Inspection...");
      return;
    }
    if (latestInspectionTrailer) {
      const assetQs = latestInspectionTrailer?.asset?.assetQuantities ?? [];
      let total = 0;
      const negatives: { id: number | string; name?: string; qty: number }[] =
        [];

      for (const q of assetQs) {
        const qty = Number(q?.quantity ?? 0);
        if (Number.isFinite(qty)) {
          total += qty;
          if (qty < 0)
            negatives.push({
              id: q.id,
              name: q.inventoryType?.name ? q.inventoryType?.name : "Unknown",
              qty,
            });
        }
      }
      if (total == 0 || negatives.length > 0) {
        toast.error(
          "Cannot start delivery with negative asset quantities, please go to load in."
        );
        setshowClockInOut(true);
        return;
      }
    }
    if (selectedJob?.id) {
      console.log("Creating delivery for job:", selectedJob.id);
      await DeliveryApi.createBaseDelivery(selectedJob.id)
        .then((res) => {
          console.log("createBaseDelivery response:", res.data);
          if (res.data.success) {
            const newDeliveryId = Number(
              res.data.affectedEntityIdentifier ?? 0
            );
            console.log("New delivery ID:", newDeliveryId);
            toast.success("Delivery Started");
            if (getSchedulerData) {
              getSchedulerData();
            }
            onCloseModal();
            if (onDeliveryStarted && newDeliveryId > 0) {
              console.log(
                "Calling onDeliveryStarted with:",
                newDeliveryId,
                selectedJob.id
              );
              onDeliveryStarted(newDeliveryId, selectedJob.id);
            } else {
              console.warn(
                "onDeliveryStarted not called - onDeliveryStarted:",
                !!onDeliveryStarted,
                "newDeliveryId:",
                newDeliveryId
              );
            }
          } else {
            console.error("createBaseDelivery failed:", res.data.message);
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          console.error("createBaseDelivery error:", err);
          toast.error("Error: " + err);
        });
    } else {
      console.error("No selectedJob.id available");
    }
  };

  function checkIsDriverAccess() {
    if (userModel && userModel.userLevelId === UserLevelNames.Driver) {
      return true;
    } else {
      return false;
    }
  }

  function checkDriverReady() {
    let driverReady = true;
    if (!userClockIn) {
      driverReady = false;
    } else if (
      !latestInspection ||
      !latestInspectionTruck ||
      !latestInspectionTrailer
    ) {
      driverReady = false;
    } else if (
      latestInspection &&
      ((latestInspection.typeId! === InspectionTypeEnum.Pre &&
        (latestInspectionTruck.sopformId === null ||
          latestInspectionTruck.sopformId === 0 ||
          latestInspectionTrailer.sopformId === null ||
          latestInspectionTrailer.sopformId === 0)) ||
        (latestInspection.typeId! === InspectionTypeEnum.Post &&
          ((latestInspectionTruck.sopformId &&
            latestInspectionTruck.sopformId > 0) ||
            (latestInspectionTrailer.sopformId &&
              latestInspectionTrailer.sopformId > 0))))
    ) {
      driverReady = false;
    }

    return driverReady;
  }

  function editClientLocation() {
    const location = clientLocationList.find(
      (x) => x.id === Number(formData.clientLocationId)
    );
    if (location) {
      setselectedClientLocation(location);
      setshowAddNewLocationModal(true);
    }
  }
  function addClientLocation() {
    setselectedClientLocation(null);
    setshowAddNewLocationModal(true);
  }

  const [showJobItemModal, setshowJobItemModal] = useState<boolean>(false);
  const [showSendNotification, setshowSendNotification] =
    useState<boolean>(false);
  const [selectedJobItem, setSelectedJobItem] = useState<JobItem | null>(null);
  const [jobItems, setjobItems] = useState<JobItem[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const editJobItem = (item: JobItem) => {
    setSelectedJobItem(item);
    setshowJobItemModal(true);
  };

  function deleteJobItem(id: number) {
    const shouldRemove = window.confirm(
      "Are you sure you want to delete this?"
    );

    if (shouldRemove) {
      JobApi.deleteJobItem(id)
        .then((res) => {
          if (res.data.success) {
            toast.success(res.data.message);
            getJobJobItemsList();
          } else {
            toast.success("Error while deleting item");
          }
        })
        .catch((err) => {
          toast.error("Error: " + err);
        });
    }
  }

  const getJobJobItemsList = useCallback(() => {
    if (selectedJob) {
      try {
        return JobApi.getJobJobItemsList(selectedJob?.id)
          .then((res) => {
            setjobItems(res.data);
          })
          .catch((err) => {
            toast.error("Error");
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error("Error");
        setIsLoading(false);
      }
    }
  }, [selectedJob]);

  useEffect(() => {
    getJobJobItemsList();
  }, [getJobJobItemsList]);

  const [locationNotes, setlocationNotes] = useState<string>("");
  const getlocationNotes = () => {
    const location = clientLocationList.find(
      (x) => x.id === Number(formData.clientLocationId ?? 0)
    );
    setlocationNotes(location?.notes ?? "");
  };

  useEffect(() => {
    getlocationNotes();
  }, [formData.clientLocationId, clientLocationList]);

  return (
    <>
      <Modal
        show={show}
        className="!max-w-none"
        style={{
          maxWidth: window.innerWidth >= 1024 ? "50vw" : "100vw",
        }}
        onClose={onCloseModal}
        popup
      >
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <div className="relative">
            {isLoading && <Loader />}
            <div className="w-full">
              <div className="w-full">
                <div className="mx-1 flex items-end justify-center">
                  <div className="flex flex-col md:flex-row flex-1 justify-between gap-2 md:gap-0">
                    <h1 className="text-gray-900 font-bold text-xl md:text-2xl lg:text-4xl">
                      {selectedJob && selectedJob.id > 0 ? "Edit" : "Add"} Job{" "}
                      {selectedJob && selectedJob.id > 0 ? selectedJob.id : ""}
                    </h1>
                    <div className="flex flex-col md:flex-row gap-2 md:gap-0">
                      {selectedJob &&
                        selectedJob.id > 0 &&
                        getSchedulerData &&
                        // (clickedDate ? clickedDate < new Date() : true) &&
                        (checkIsDriverAccess() ? checkDriverReady() : true) && (
                          <Button
                            disabled={buttonDisabled}
                            type="button"
                            className="buttonMainAlt2"
                            onClick={() => {
                              if (!checkDriverReady()) {
                                setshowClockInOut(true);
                                toast.error(
                                  "Please complete your Pre-Trip Inspection..."
                                );
                                return;
                              }
                              setshowSendNotification(true);
                            }}
                          >
                            Send Notification
                          </Button>
                        )}
                      {selectedJob &&
                        selectedJob.id > 0 &&
                        getSchedulerData && (
                          // (clickedDate ? clickedDate < new Date() : true) &&
                          <Button
                            disabled={buttonDisabled}
                            type="button"
                            className="buttonMain"
                            onClick={createDelivery}
                          >
                            Start Delivery
                          </Button>
                        )}
                      {!checkDriverReady() && (
                        <div className="cursor-not-allowed bg-red-600 text-white rounded-md text-center my-auto p-1 font-bold">
                          <Button
                            type="button"
                            onClick={() => setshowClockInOut(true)}
                          >
                            Complete Pre-Inspection to begin deliveries
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="container mx-auto p-2">
                  <div className="md:flex no-wrap md:-mx-2 ">
                    <div className="w-full">
                      <div className="bg-white">
                        <hr className="bg-gray-300 h-[5px]"></hr>
                        <form id="jobForm" onSubmit={onSubmit}>
                          <div className="text-gray-500">
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Client:
                                </div>
                              </div>
                              <div className="w-full">
                                <FBSelect
                                  id="clientId"
                                  name="clientId"
                                  onChange={handleChangeSelect}
                                  value={formData.clientId ?? ""}
                                  sizing="md"
                                  required
                                  disabled={checkIsDriverAccess()}
                                >
                                  <option value={""}>(Select)</option>
                                  {clientList
                                    .sort((a, b) =>
                                      (a.name ?? "").localeCompare(b.name ?? "")
                                    )
                                    .map((type) => (
                                      <option key={type.id} value={type.id}>
                                        {type.name}
                                      </option>
                                    ))}
                                </FBSelect>
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Location:
                                </div>
                              </div>
                              <div className="flex flex-col md:flex-row gap-2 md:gap-0">
                                <FBSelect
                                  id="clientLocationId"
                                  name="clientLocationId"
                                  onChange={handleChangeSelect}
                                  value={formData.clientLocationId ?? ""}
                                  sizing="md"
                                  required
                                  className="w-full"
                                  disabled={checkIsDriverAccess()}
                                >
                                  <option value={""}>(Select)</option>
                                  {clientLocationList
                                    .sort(
                                      (a, b) =>
                                        (a.city ?? "").localeCompare(
                                          b.city ?? ""
                                        ) ||
                                        (a.address ?? "").localeCompare(
                                          b.address ?? ""
                                        )
                                    )
                                    .map((type) => (
                                      <option key={type.id} value={type.id}>
                                        {type.clientSiteId} {": "}{" "}
                                        {type.address} {type.city}{" "}
                                        {type.state ? type.state.name : ""}
                                      </option>
                                    ))}
                                </FBSelect>
                                {formData.clientLocationId &&
                                  formData.clientLocationId > 0 && (
                                    <div className="content-center pl-2">
                                      <button
                                        onClick={() => editClientLocation()}
                                        className="cursor-pointer text-blue-600"
                                        type="button"
                                        disabled={checkIsDriverAccess()}
                                      >
                                        Edit
                                      </button>
                                    </div>
                                  )}
                                {formData.clientId && formData.clientId > 0 && (
                                  <div className="content-center pl-2">
                                    <Button
                                      onClick={() => addClientLocation()}
                                      className="buttonMain"
                                      disabled={checkIsDriverAccess()}
                                    >
                                      <HiPlus size={12} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Contact Name:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="contactName"
                                  name="contactName"
                                  type="text"
                                  placeholder="Contact Name..."
                                  value={
                                    formData.clientLocation?.contactName ?? ""
                                  }
                                  sizing="md"
                                  disabled={true}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Contact Phone:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="contactPhone"
                                  name="contactPhone"
                                  type="text"
                                  placeholder="Contact Phone..."
                                  value={
                                    formData.clientLocation?.contactPhone ?? ""
                                  }
                                  sizing="md"
                                  disabled={true}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Contact Email:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="contactEmail"
                                  name="contactEmail"
                                  type="text"
                                  placeholder="Contact Email..."
                                  value={
                                    formData.clientLocation?.contactEmail ?? ""
                                  }
                                  sizing="md"
                                  disabled={true}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-xs md:text-sm lg:text-lg min-h-[42px] gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Location Notes:
                                </div>
                              </div>
                              <div className="w-full content-center text-black">
                                {locationNotes}
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-1 md:grid-cols-[35%_65%] text-sm md:text-lg lg:text-xl gap-2 md:gap-0">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Driver:
                                </div>
                              </div>
                              <div className="w-full">
                                <FBSelect
                                  id="userId"
                                  name="userId"
                                  onChange={handleChangeSelect}
                                  value={formData.userId ?? ""}
                                  sizing="md"
                                  disabled={checkIsDriverAccess()}
                                >
                                  <option value={""}>(Select)</option>
                                  {[...users]
                                    .sort((a, b) =>
                                      (a.firstName ?? "").localeCompare(
                                        b.firstName ?? ""
                                      )
                                    )
                                    .map((obj) => (
                                      <option key={obj.id} value={obj.id}>
                                        {obj.firstName} {obj.lastName}
                                      </option>
                                    ))}
                                </FBSelect>
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  PO Number:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="jobNumber"
                                  name="jobNumber"
                                  type="text"
                                  placeholder="Name..."
                                  onChange={handleChangeInput}
                                  value={formData.jobNumber ?? ""}
                                  sizing="md"
                                  disabled={checkIsDriverAccess()}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Start Date:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="startDateOnly"
                                  name="startDateOnly"
                                  type="date"
                                  placeholder="Date..."
                                  onChange={handleDateChange}
                                  value={startDateOnly}
                                  sizing="md"
                                  disabled={checkIsDriverAccess()}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Drop Time:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="dropTime"
                                  name="dropTime"
                                  type="time"
                                  placeholder="Time..."
                                  onChange={handleTimeChange}
                                  value={dropTime}
                                  sizing="md"
                                  disabled={checkIsDriverAccess()}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Recurring:
                                </div>
                              </div>
                              <div className="w-full flex gap-5">
                                <div className="flex items-center gap-2">
                                  <Radio
                                    id="isRecurring"
                                    name="isRecurring"
                                    value={"true"}
                                    checked={formData.isRecurring ?? false}
                                    onChange={handleChangeInput}
                                    disabled={checkIsDriverAccess()}
                                  />
                                  <div className="font-bold content-center text-sm">
                                    Yes
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Radio
                                    id="isRecurring"
                                    name="isRecurring"
                                    value={"false"}
                                    checked={!(formData.isRecurring ?? false)}
                                    onChange={handleChangeInput}
                                    disabled={checkIsDriverAccess()}
                                  />
                                  <div className="font-bold content-center text-sm">
                                    No
                                  </div>
                                </div>
                              </div>
                            </div>
                            {formData.isRecurring && (
                              <div>
                                <hr className="bg-gray-300 h-[2px]"></hr>
                                <div className="grid grid-cols-[35%_65%] text-xl">
                                  <div className="flex">
                                    <div className="font-bold content-center">
                                      End Date:
                                    </div>
                                  </div>
                                  <div className="w-full">
                                    <TextInput
                                      id="endDate"
                                      name="endDate"
                                      type="date"
                                      placeholder="Date..."
                                      onChange={handleChangeInput}
                                      value={formatDateInputs(
                                        formData.endDate?.toString() ?? ""
                                      )}
                                      sizing="md"
                                      pattern="\d{4}-\d{2}-\d{2}"
                                      disabled={checkIsDriverAccess()}
                                    />
                                  </div>
                                </div>
                                <hr className="bg-gray-300 h-[2px]"></hr>
                                <div className="grid grid-cols-[35%_65%] text-xl">
                                  <div className="flex">
                                    <div className="font-bold content-center">
                                      Frequency:
                                    </div>
                                  </div>
                                  <div className="w-full">
                                    <FBSelect
                                      id="frequencyId"
                                      name="frequencyId"
                                      onChange={handleChangeSelect}
                                      value={formData.frequencyId ?? ""}
                                      sizing="md"
                                      disabled={checkIsDriverAccess()}
                                    >
                                      <option value={""}>(Select)</option>
                                      {frequencies.map((type) => (
                                        <option key={type.id} value={type.id}>
                                          {type.name}
                                        </option>
                                      ))}
                                    </FBSelect>
                                  </div>
                                </div>
                                <hr className="bg-gray-300 h-[2px]"></hr>
                                <div className="grid grid-cols-[35%_65%] text-xl">
                                  <div className="flex">
                                    <div className="font-bold content-center">
                                      Day:
                                    </div>
                                  </div>
                                  <div className="w-full">
                                    <FBSelect
                                      id="day"
                                      name="day"
                                      onChange={handleChangeSelect}
                                      value={formData.day ?? ""}
                                      sizing="md"
                                      disabled={checkIsDriverAccess()}
                                    >
                                      <option value={""}>(Select)</option>
                                      <option key={0} value={0}>
                                        Sunday
                                      </option>
                                      <option key={1} value={1}>
                                        Monday
                                      </option>
                                      <option key={2} value={2}>
                                        Tuesday
                                      </option>
                                      <option key={3} value={3}>
                                        Wednesday
                                      </option>
                                      <option key={4} value={4}>
                                        Thursday
                                      </option>
                                      <option key={5} value={5}>
                                        Friday
                                      </option>
                                      <option key={6} value={6}>
                                        Saturday
                                      </option>
                                    </FBSelect>
                                  </div>
                                </div>
                              </div>
                            )}
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Max Amount:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="maxAmount"
                                  name="maxAmount"
                                  type="text"
                                  placeholder="0.00"
                                  onChange={handleChangeCurrencyInput}
                                  onKeyDown={(event) => {
                                    if (onlyNumberInput(event)) {
                                      event.preventDefault();
                                    }
                                  }}
                                  value={formData.maxAmount ?? ""}
                                  sizing="md"
                                  addon="$"
                                  disabled={checkIsDriverAccess()}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Terms:
                                </div>
                              </div>
                              <div className="w-full">
                                <TextInput
                                  id="terms"
                                  name="terms"
                                  type="text"
                                  placeholder="Terms..."
                                  onChange={handleChangeInput}
                                  value={formData.terms ?? ""}
                                  sizing="md"
                                  disabled={checkIsDriverAccess()}
                                />
                              </div>
                            </div>
                            <hr className="bg-gray-300 h-[2px]"></hr>
                            <div className="grid grid-cols-[35%_65%] text-xl">
                              <div className="flex">
                                <div className="font-bold content-center">
                                  Notes:
                                </div>
                              </div>
                              <div className="w-full">
                                <Textarea
                                  id="notes"
                                  name="notes"
                                  placeholder="Notes..."
                                  onChange={handleChangeTextArea}
                                  value={formData.notes ?? ""}
                                  rows={3}
                                  disabled={checkIsDriverAccess()}
                                  autoComplete="new-password"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end pt-2">
                            <Button
                              disabled={buttonDisabled}
                              type="submit"
                              className="buttonMain"
                              form="jobForm"
                              // onClick={() => onSubmit()}
                            >
                              Save
                            </Button>
                          </div>
                        </form>
                        {selectedJob && selectedJob.id > 0 && (
                          <>
                            <div className="border-t-2 mt-2">
                              <div className="w-full h-[74px] p-4">
                                <div className="mx-1 flex items-end">
                                  <div className="flex flex-1">
                                    <h1 className="text-gray-900 font-bold text-xl">
                                      <div>Items</div>
                                    </h1>
                                  </div>
                                  <div className="">
                                    <Button
                                      onClick={() => setshowJobItemModal(true)}
                                      className="buttonMain"
                                    >
                                      <HiPlus size={12} />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              <JobItemListTable
                                data={jobItems}
                                deleteJobItem={deleteJobItem}
                                editJobItem={editJobItem}
                              />
                            </div>
                            <JobItemModal
                              show={showJobItemModal}
                              setShow={setshowJobItemModal}
                              jobId={selectedJob.id}
                              getJobItems={getJobJobItemsList}
                              getSchedulerData={getSchedulerData}
                              selectedJobItem={selectedJobItem}
                              setSelectedJobItem={setSelectedJobItem}
                            />

                            {selectedJob &&
                              selectedJob.id > 0 &&
                              checkIsDriverAccess() == false && (
                                <Button
                                  color={"red"}
                                  type="button"
                                  className="buttonMainAlt2"
                                  onClick={deleteJob}
                                >
                                  Delete Job
                                </Button>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {formData.clientId && (
                <ClientLocationModal
                  show={showAddNewLocationModal}
                  setShow={setshowAddNewLocationModal}
                  clientId={formData.clientId}
                  getClientLocations={getClientLocations}
                  selectedClientLocation={selectedClientLocation}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <SendNotificationModal
        show={showSendNotification}
        setShow={setshowSendNotification}
        selectedJob={selectedJob}
      />
      <DriverClockInOut show={showClockInOut} setShow={setshowClockInOut} />

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        size="md"
        popup
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200">
              <svg
                className="mx-auto h-14 w-14 text-gray-400 dark:text-gray-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Are you sure you want to delete this job? This action can be
              undone by contacting support.
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={confirmDeleteJob}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setShowDeleteConfirm(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
